import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Star, ExternalLink, X, Gamepad2, Calendar, Users, Globe, Settings, FileText, Download } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { FunnyLoader } from "@/components/FunnyLoader";
import { MetaTags } from "@/components/MetaTags";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /youtube\.com\/shorts\/([^&\s?]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return `https://www.youtube.com/embed/${match[1]}?rel=0&modestbranding=1`;
    }
  }
  return null;
};

export default function GamePage() {
  const { gameId } = useParams();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [currentReview, setCurrentReview] = useState(0);
  const [bgHeight, setBgHeight] = useState('100vh');
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Fix background height on mount to prevent resize on mobile scroll
  useEffect(() => {
    // Set height once on mount based on window.innerHeight
    const setFixedHeight = () => {
      // Add extra pixels to cover address bar hide/show
      const height = Math.max(window.innerHeight, document.documentElement.clientHeight) + 100;
      setBgHeight(`${height}px`);
    };
    
    setFixedHeight();
    
    // Only update on orientation change, not on scroll
    const handleOrientationChange = () => {
      setTimeout(setFixedHeight, 100);
    };
    
    window.addEventListener('orientationchange', handleOrientationChange);
    return () => window.removeEventListener('orientationchange', handleOrientationChange);
  }, []);

  // Auto-rotate reviews on mobile every 4 seconds
  useEffect(() => {
    if (!game?.testimonials || game.testimonials.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentReview((prev) => (prev + 1) % Math.min(game.testimonials.length, 3));
    }, 4000);
    
    return () => clearInterval(interval);
  }, [game?.testimonials]);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    const testimonialCount = Math.min(game?.testimonials?.length || 0, 3);
    
    if (Math.abs(diff) > 50) { // Minimum swipe distance
      if (diff > 0) {
        // Swipe left - next
        setCurrentReview((prev) => (prev + 1) % testimonialCount);
      } else {
        // Swipe right - previous
        setCurrentReview((prev) => (prev - 1 + testimonialCount) % testimonialCount);
      }
    }
  }, [game?.testimonials]);

  useEffect(() => {
    const fetchGame = async () => {
      try {
        const res = await axios.get(`${API}/games/${gameId}/full`);
        setGame(res.data);
      } catch (error) {
        console.error("Error fetching game:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchGame();
    window.scrollTo(0, 0);
  }, [gameId]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center pt-20">
          <FunnyLoader />
        </main>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="text-6xl mb-4">😕</div>
            <p className="text-white text-xl mb-4">Juego no encontrado</p>
            <a href="/juegos" className="text-[#E91E8C] hover:underline">
              ← Volver a Juegos
            </a>
          </div>
        </main>
      </div>
    );
  }

  const youtubeEmbedUrl = getYoutubeEmbedUrl(game.trailer_url);

  return (
    <div 
      className="min-h-screen flex flex-col relative" 
      data-testid="game-page"
    >
      <MetaTags 
        title={game.title}
        description={game.tagline || game.description?.substring(0, 160)}
        image={game.cover_url || game.thumbnail_url || game.promo_image_url}
        url={`/juegos/${game.slug || game.id}`}
      />
      {/* Fixed Background - Height set once on mount to prevent mobile resize */}
      <div 
        className="fixed top-0 left-0 w-full z-0"
        style={{
          height: bgHeight,
          backgroundImage: game.background_url ? `url(${game.background_url})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#1a1a1a'
        }}
      />
      
      {/* Semi-transparent gradient overlay */}
      <div 
        className="fixed top-0 left-0 w-full z-0 pointer-events-none"
        style={{
          height: bgHeight,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.55) 30%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.75) 100%)'
        }}
      />

      {/* All content needs z-index above background */}
      <div className="relative z-10 flex flex-col min-h-screen">
        <Navbar />

      {/* Main Content - Single column like Tunic */}
      <main className="flex-1 pt-40 pb-16">
        <div className="max-w-4xl mx-auto px-4 text-center">

          {/* Logo or Title */}
          <div className="mb-8">
            {game.logo_url ? (
              <img 
                src={game.logo_url}
                alt={game.title}
                className="mx-auto drop-shadow-2xl"
                style={{ maxHeight: '172px', width: 'auto', height: 'auto' }}
              />
            ) : (
              <h1 
                className="text-5xl md:text-7xl font-black text-white uppercase tracking-wider"
                style={{ textShadow: '4px 4px 0 #000, -2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000' }}
              >
                {game.title}
              </h1>
            )}
          </div>

          {/* Video Trailer or Hero Image */}
          {youtubeEmbedUrl ? (
            <div className="mb-8">
              <div 
                className="w-full aspect-video overflow-hidden shadow-2xl"
                style={{ 
                  border: '4px solid rgba(255,255,255,0.3)',
                  borderRadius: '4px'
                }}
              >
                <iframe
                  src={youtubeEmbedUrl}
                  title={`${game.title} Trailer`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          ) : (game.hero_image_url || game.promo_image_url || game.cover_url) && (
            <div className="mb-8">
              <div 
                className="w-full overflow-hidden shadow-2xl"
                style={{ 
                  border: '4px solid rgba(255,255,255,0.3)',
                  borderRadius: '4px'
                }}
              >
                <img 
                  src={game.hero_image_url || game.promo_image_url || game.cover_url}
                  alt={game.title}
                  className="w-full h-auto"
                />
              </div>
            </div>
          )}

          {/* Store Buttons - Below video */}
          {game.stores && game.stores.length > 0 && (
            <div className="mb-8">
              <p 
                className="text-white font-black text-xl mb-4 uppercase"
                style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}
              >
                {game.status === 'coming_soon' ? '¡Próximamente!' : '¡Disponible ahora!'}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                {game.stores.map((store) => {
                  // Map store names to Font Awesome icons
                  const getStoreIcon = (storeName) => {
                    const name = storeName.toLowerCase();
                    if (name.includes('google') || name.includes('play')) return 'fa-brands fa-google-play';
                    if (name.includes('apple') || name.includes('app store') || name.includes('ios')) return 'fa-brands fa-apple';
                    if (name.includes('steam')) return 'fa-brands fa-steam';
                    if (name.includes('itch')) return 'fa-brands fa-itch-io';
                    if (name.includes('xbox')) return 'fa-brands fa-xbox';
                    if (name.includes('playstation') || name.includes('ps')) return 'fa-brands fa-playstation';
                    if (name.includes('nintendo') || name.includes('switch')) return 'fa-solid fa-gamepad';
                    if (name.includes('epic')) return 'fa-solid fa-e';
                    if (name.includes('amazon')) return 'fa-brands fa-amazon';
                    return 'fa-solid fa-download';
                  };

                  const hasUrl = store.url && store.url.trim() !== '';
                  
                  if (hasUrl) {
                    return (
                      <a
                        key={store.id}
                        href={store.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 md:gap-3 px-4 py-3 md:px-8 md:py-5 bg-black text-white font-bold uppercase text-sm md:text-xl hover:scale-105 transition-transform hand-drawn-subtle"
                        style={{ 
                          borderWidth: '3px', 
                          borderColor: '#fff', 
                          borderStyle: 'solid',
                          boxShadow: '0 4px 15px rgba(0,0,0,0.4)' 
                        }}
                      >
                        <i className={`${getStoreIcon(store.store_name)} text-lg md:text-2xl`}></i>
                        {store.button_text || store.store_name}
                        {store.price && store.price !== 'Gratis' && (
                          <span className="opacity-80">({store.price})</span>
                        )}
                      </a>
                    );
                  } else {
                    // No URL - show disabled/greyed out button with strikethrough
                    return (
                      <div
                        key={store.id}
                        className="flex items-center justify-center gap-2 md:gap-3 px-4 py-3 md:px-8 md:py-5 bg-gray-800 text-gray-500 font-bold uppercase text-sm md:text-xl cursor-not-allowed hand-drawn-subtle line-through"
                        style={{ 
                          borderWidth: '3px', 
                          borderColor: '#666', 
                          borderStyle: 'solid',
                          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                          opacity: 0.6
                        }}
                      >
                        <i className={`${getStoreIcon(store.store_name)} text-lg md:text-2xl`}></i>
                        {store.button_text || store.store_name}
                        {store.price && store.price !== 'Gratis' && (
                          <span className="opacity-80">({store.price})</span>
                        )}
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          )}

          {/* Instructions PDF Download - For board games */}
          {game.instructions_pdf_url && (
            <div className="mb-8">
              <a
                href={`${game.instructions_pdf_url}?download=true`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 px-6 py-4 bg-white/10 backdrop-blur-sm text-white font-bold uppercase text-lg hover:bg-white/20 transition-all hand-drawn-subtle"
                style={{ 
                  borderWidth: '3px', 
                  borderColor: 'rgba(255,255,255,0.5)', 
                  borderStyle: 'solid',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.3)' 
                }}
              >
                <FileText size={24} />
                Descargar Instructivo
                <Download size={20} />
              </a>
            </div>
          )}

          {/* Testimonials - Below store buttons */}
          {game.testimonials && game.testimonials.length > 0 && (
            <div className="mb-8">
              {/* Mobile Carousel */}
              <div 
                className="md:hidden relative overflow-hidden"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                <div 
                  className="flex transition-transform duration-300 ease-in-out"
                  style={{ transform: `translateX(-${currentReview * 100}%)` }}
                >
                  {game.testimonials.slice(0, 3).map((testimonial) => (
                    <div 
                      key={testimonial.id} 
                      className="w-full flex-shrink-0 text-center py-4 px-2"
                    >
                      {testimonial.rating && (
                        <div className="flex justify-center gap-1 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i}
                              size={18}
                              className={i < testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-white/30'}
                            />
                          ))}
                        </div>
                      )}
                      <p 
                        className="text-white italic text-lg mb-2"
                        style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}
                      >
                        "{testimonial.quote}"
                      </p>
                      <p 
                        className="text-white/80 text-sm"
                        style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}
                      >
                        <span className="font-bold">— {testimonial.author}</span>
                        {testimonial.source && <span> · {testimonial.source}</span>}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Desktop Grid */}
              <div className="hidden md:grid md:grid-cols-3 gap-4">
                {game.testimonials.slice(0, 3).map((testimonial) => (
                  <div 
                    key={testimonial.id} 
                    className="text-center py-4"
                  >
                    {testimonial.rating && (
                      <div className="flex justify-center gap-1 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i}
                            size={18}
                            className={i < testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-white/30'}
                          />
                        ))}
                      </div>
                    )}
                    <p 
                      className="text-white italic text-base mb-2"
                      style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}
                    >
                      "{testimonial.quote}"
                    </p>
                    <p 
                      className="text-white/80 text-sm"
                      style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}
                    >
                      <span className="font-bold">— {testimonial.author}</span>
                      {testimonial.source && <span> · {testimonial.source}</span>}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Promo Image (Character/Art) - Show if we have a dedicated promo image */}
          {/* Shows when: has trailer (promo not used as hero) OR has hero_image (promo not used as hero) */}
          {game.promo_image_url && (youtubeEmbedUrl || game.hero_image_url) && (
            <div className="mb-8 flex justify-center">
              <img 
                src={game.promo_image_url}
                alt={`${game.title} character`}
                className="max-w-full max-h-96 object-contain drop-shadow-2xl"
                style={{ filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.5))' }}
              />
            </div>
          )}

          {/* Description */}
          {game.description && (
            <div className="mb-8 text-center">
              <p 
                className="text-white text-lg leading-relaxed whitespace-pre-line"
                style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}
              >
                {game.description}
              </p>
            </div>
          )}

          {/* Gallery - Above Features */}
          {game.gallery && game.gallery.length > 0 && (
            <div className="mb-8">
              {/* Mobile: max 4 images */}
              <div className="grid grid-cols-2 gap-3 md:hidden">
                {game.gallery.slice(0, 4).map((item) => (
                  <div 
                    key={item.id}
                    className="relative aspect-video cursor-pointer group overflow-hidden hand-drawn-subtle"
                    style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
                    onClick={() => setLightboxImage(item)}
                  >
                    <img 
                      src={item.image_url}
                      alt={item.caption || "Screenshot"}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    {item.is_gif && (
                      <div className="absolute top-1 right-1 px-2 py-0.5 bg-[#E91E8C] text-white text-xs font-bold rounded">
                        GIF
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Desktop: max 3 images */}
              <div className="hidden md:grid md:grid-cols-3 gap-3">
                {game.gallery.slice(0, 3).map((item) => (
                  <div 
                    key={item.id}
                    className="relative aspect-video cursor-pointer group overflow-hidden hand-drawn-subtle"
                    style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
                    onClick={() => setLightboxImage(item)}
                  >
                    <img 
                      src={item.image_url}
                      alt={item.caption || "Screenshot"}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    {item.is_gif && (
                      <div className="absolute top-1 right-1 px-2 py-0.5 bg-[#E91E8C] text-white text-xs font-bold rounded">
                        GIF
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Features */}
          {game.features && game.features.length > 0 && (
            <div className="mb-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {game.features.map((feature) => (
                  <div key={feature.id} className="text-center p-3">
                    {feature.icon && (
                      <div className="text-3xl mb-2">{feature.icon}</div>
                    )}
                    <h3 
                      className="text-white font-bold text-sm uppercase"
                      style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}
                    >
                      {feature.title}
                    </h3>
                    {feature.description && (
                      <p 
                        className="text-white/70 text-xs mt-1"
                        style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}
                      >
                        {feature.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Technical Info */}
          {(game.genre || game.players || game.languages || game.release_date) && (
            <div className="mb-8">
              <h2 
                className="text-white font-black text-xl mb-4 uppercase text-center"
                style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}
              >
                Información
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {game.genre && (
                  <div className="text-center">
                    <Gamepad2 className="mx-auto mb-1 text-white" size={20} style={{ filter: 'drop-shadow(2px 2px 2px rgba(0,0,0,0.8))' }} />
                    <p className="text-white/60 text-xs uppercase" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>Género</p>
                    <p className="text-white font-bold text-sm" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>{game.genre}</p>
                  </div>
                )}
                {game.players && (
                  <div className="text-center">
                    <Users className="mx-auto mb-1 text-white" size={20} style={{ filter: 'drop-shadow(2px 2px 2px rgba(0,0,0,0.8))' }} />
                    <p className="text-white/60 text-xs uppercase" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>Jugadores</p>
                    <p className="text-white font-bold text-sm" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>{game.players}</p>
                  </div>
                )}
                {game.languages && (
                  <div className="text-center">
                    <Globe className="mx-auto mb-1 text-white" size={20} style={{ filter: 'drop-shadow(2px 2px 2px rgba(0,0,0,0.8))' }} />
                    <p className="text-white/60 text-xs uppercase" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>Idiomas</p>
                    <p className="text-white font-bold text-sm" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>{game.languages}</p>
                  </div>
                )}
                {game.release_date && (
                  <div className="text-center">
                    <Calendar className="mx-auto mb-1 text-white" size={20} style={{ filter: 'drop-shadow(2px 2px 2px rgba(0,0,0,0.8))' }} />
                    <p className="text-white/60 text-xs uppercase" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>Lanzamiento</p>
                    <p className="text-white font-bold text-sm" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>{game.release_date}</p>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </main>

      {/* Transparent Footer for Games */}
      <footer className="py-8 px-4 relative z-10">
        <div className="max-w-7xl mx-auto text-center">
          <a 
            href="/admin"
            className="text-sm text-white/50 hover:text-white/70 transition-colors"
            style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}
          >
            Copyright © {new Date().getFullYear()} Darkar Company Studios, LLC.
          </a>
        </div>
      </footer>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-[#E91E8C] transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <X size={32} />
          </button>
          <img 
            src={lightboxImage.image_url}
            alt={lightboxImage.caption || "Screenshot"}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
